/**
 * Helpers for importing a Zoom meeting CDR (the per-participant CSV export)
 * into the learn-time upload screen.
 *
 * Zoom reports durations as whole minutes. The backend stores durations as
 * "HH:MM:SS" strings and parses them with formatHHMMSSToSecond, which splits
 * on ":". Sending Zoom's bare "398" would therefore be read as 398 HOURS, so
 * every duration crossing this boundary must be formatted here.
 */

function pad2(value) {
  return value < 10 ? '0' + value : String(value);
}

/**
 * @param {number|string} minutes whole minutes, as Zoom reports them
 * @returns {string} "HH:MM:SS"; "00:00:00" for anything unusable
 */
export function minutesToHHMMSS(minutes) {
  const total = Number(minutes);
  if (!isFinite(total) || total <= 0) return '00:00:00';

  const whole = Math.floor(total);
  const hours = Math.floor(whole / 60);
  const mins = whole % 60;

  return pad2(hours) + ':' + pad2(mins) + ':00';
}

/**
 * The backend strips every space from participantName before matching
 * (add-upload-yearlink-learn-time.js:263). Mirrored here so the on-screen
 * summary groups rows exactly the way the upload will.
 */
export function stripSpaces(name) {
  return String(name).split(' ').join('');
}

/**
 * The backend's findStudentFromCode (add-upload-yearlink-learn-time.js:9-15)
 * takes the first 10-digit run. An 11-digit typo is therefore truncated, which
 * can resolve to a different student -- see section 9a of the spec. Reproduced
 * faithfully so staff see on screen what the backend will really use.
 *
 * @returns {string|null}
 */
export function extractStudentCode(name) {
  const found = stripSpaces(name).match(/[0-9]{10}/);
  return found ? found[0] : null;
}

/**
 * Header cells a Zoom CDR must carry. 'Duration (minutes)' is listed TWICE on
 * purpose: Zoom emits it at meeting level and again at participant level, and
 * sheet_to_json only produces the 'Duration (minutes)_1' key when both exist.
 */
export const ZOOM_REQUIRED_COLUMNS = [
  'Topic',
  'Type',
  'ID',
  'Host name',
  'Start time',
  'End time',
  'Participants',
  'Duration (minutes)',
  'Group',
  'Name (original name)',
  'Join time',
  'Leave time',
  'Duration (minutes)',
];

/** Cells that must arrive as Excel serial numbers. */
export const ZOOM_DATE_COLUMNS = ['Start time', 'End time', 'Join time', 'Leave time'];

const MAX_REPORTED_BAD_CELLS = 10;

/**
 * @param {string[]} header raw header cells, still containing duplicates
 * @returns {{valid: boolean, missing: string[]}}
 */
export function validateZoomHeader(header) {
  if (!header || !header.length) {
    return { valid: false, missing: ZOOM_REQUIRED_COLUMNS.slice() };
  }

  const seen = {};
  header.forEach(cell => {
    const key = String(cell).trim();
    seen[key] = (seen[key] || 0) + 1;
  });

  const needed = {};
  ZOOM_REQUIRED_COLUMNS.forEach(cell => {
    needed[cell] = (needed[cell] || 0) + 1;
  });

  const missing = Object.keys(needed).filter(cell => (seen[cell] || 0) < needed[cell]);
  return { valid: missing.length === 0, missing };
}

/**
 * Blocks a file whose date cells are not Excel serial numbers.
 *
 * Letting one through hits add-upload-yearlink-learn-time.js:424-458, which
 * sets element.remark and then spreads element into a Prisma create. The
 * YeaLinkImportData model has no remark field, so Prisma rejects the batch --
 * after saveImportData has already flushed earlier batches of 100, leaving
 * partial writes. An empty cell is quieter and worse: '' - 25569 coerces to
 * -25569, a valid-looking 1900 date that is stored without complaint.
 *
 * @param {object[]} rows sheet_to_json output
 * @returns {{valid: boolean, badCells: Array<{rowNo: number, column: string, value: *}>}}
 */
export function validateZoomRows(rows) {
  if (!rows || !rows.length) return { valid: false, badCells: [] };

  const badCells = [];
  for (let i = 0; i < rows.length; i++) {
    for (let c = 0; c < ZOOM_DATE_COLUMNS.length; c++) {
      const column = ZOOM_DATE_COLUMNS[c];
      const value = rows[i][column];
      if (typeof value !== 'number' || !isFinite(value)) {
        // Header occupies spreadsheet row 1, so rows[0] is row 2.
        badCells.push({ rowNo: i + 2, column: column, value: value });
        if (badCells.length >= MAX_REPORTED_BAD_CELLS) {
          return { valid: false, badCells: badCells };
        }
      }
    }
  }

  return { valid: badCells.length === 0, badCells: badCells };
}

/** Column holding the PARTICIPANT duration. sheet_to_json appends _1 because
 *  Zoom emits 'Duration (minutes)' at meeting level first. */
const PARTICIPANT_DURATION = 'Duration (minutes)_1';
const MEETING_DURATION = 'Duration (minutes)';
const PARTICIPANT_NAME = 'Name (original name)';

function text(value) {
  return value === null || value === undefined ? '' : String(value);
}

/**
 * Maps one sheet_to_json row to a YeaLinkImportDataInput.
 *
 * Columns Zoom does not export (device, IP, role, calling type) are sent as
 * empty strings rather than invented values.
 * maximumNumberOfConcurrentParties is omitted entirely: the schema types it as
 * Int and Zoom reports '-'.
 */
export function mapZoomRowToImportData(row) {
  return {
    participantName: text(row[PARTICIPANT_NAME]),
    meetingNo: text(row['ID']),
    meetingTheme: text(row['Topic']).replace(/\s+/g, ' ').trim(),
    meetingType: text(row['Type']),
    grouping: text(row['Group']),
    meetingRoom: text(row['Host name']),
    mode: 'ZOOM',
    startTime: text(row['Start time']),
    endTime: text(row['End time']),
    meetingTime: text(row['Join time']),
    departureTime: text(row['Leave time']),
    stayDuration: minutesToHHMMSS(row[PARTICIPANT_DURATION]),
    meetingDuration: minutesToHHMMSS(row[MEETING_DURATION]),
    numberOfParticipants: parseInt(row['Participants'], 10) || 0,
    accountType: '',
    rolePermissions: '',
    deviceModel: '',
    ipAddress: '',
    callingType: '',
  };
}

/**
 * Groups rows the way the upload will: by student code, and -- for rows with
 * no code -- by the display name, so staff can tell the host apart from an
 * unnamed guest.
 *
 * Zoom writes one row per join/leave, so a student who reconnects appears
 * several times. The backend sums those already; this shows the same total
 * before anything is sent.
 */
export function summarizeByStudent(rows) {
  if (!rows || !rows.length) return [];

  const groups = {};
  const order = [];

  rows.forEach(row => {
    const rawName = text(row[PARTICIPANT_NAME]);
    const studentCode = extractStudentCode(rawName);
    // Uncoded rows cannot be merged: two different people both lacking a code
    // are not the same student.
    const key = studentCode ? 'CODE:' + studentCode : 'NAME:' + stripSpaces(rawName);

    if (!groups[key]) {
      groups[key] = {
        studentCode: studentCode,
        names: [],
        sessionCount: 0,
        totalMinutes: 0,
        totalDuration: '00:00:00',
      };
      order.push(key);
    }

    const group = groups[key];
    if (group.names.indexOf(rawName) < 0) group.names.push(rawName);
    group.sessionCount += 1;

    const minutes = Number(row[PARTICIPANT_DURATION]);
    if (isFinite(minutes) && minutes > 0) group.totalMinutes += Math.floor(minutes);
  });

  return order
    .map(key => {
      const group = groups[key];
      group.totalDuration = minutesToHHMMSS(group.totalMinutes);
      return group;
    })
    .sort((a, b) => b.totalMinutes - a.totalMinutes);
}
